import React, { memo } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { useLang } from 'common/hooks';
import { getDomain } from 'utils';
import { useSuggestionsButtonAction } from '../../hooks/use-suggestions-button-action';
import { SuggestionsDefaultListPinnedItems } from '../suggestions-list/suggestions-default-list-pinned-items';
import { useSiteDomain } from '../../hooks/use-site-domain';
import { useSponsoredProducts } from 'components/header/hooks';
import { CategoriesList } from '../categories-list';
import { useSponsoredBrands } from '../../hooks';
import { CompletionsAkinator } from '../akinator';
import { getUpdatedQueryByCompletionType } from '../utils/get-update-query-by-completion-type';
import { getDefaultListPinnedOffset } from '../utils/get-default-list-navigable-pinned-items';
import { NewSuggestionsContent } from './new-suggestions-content';
import { AkinatorCompletion, SlicedSuggestions } from 'types/suggestions';
import { PinnedSuggestion } from 'types/model';

type SuggestionsProps = {
  searchQuery: string;
  updateSearchQuery: (value: string) => void;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  onSelectSuggestion?: () => void;
  searchHref: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  pinnedSuggestions: PinnedSuggestion[];
  completions: AkinatorCompletion[] | null;
  showAkinatorCompletions: boolean;
};

export const Suggestions = memo<SuggestionsProps>(
  ({
    searchQuery,
    updateSearchQuery,
    suggestions,
    suggestionSelector,
    onMouseEnter,
    onMouseLeave,
    searchHref,
    index,
    isSrp,
    onSelectSuggestion,
    pinnedSuggestions,
    completions,
    showAkinatorCompletions,
  }) => {
    const [newSuggestions, slicedCategories] = suggestions ?? [[], []];
    const sponsoredProducts = useSponsoredProducts();
    const hasSponsoredProducts = sponsoredProducts.length > 0;
    const { itemsToDisplay: sponsoredBrands } = useSponsoredBrands();
    const { host } = useStoreon<State>('host');
    const domain = getDomain(host);
    const { isKzDomain } = useSiteDomain(domain);
    const { lang } = useLang();
    const showProducts = !(isKzDomain && lang === 'kk');
    const isDefaultList = searchQuery === '';
    const showOnlyCategories = isDefaultList && slicedCategories.length > 0;
    const akinatorOffset = showAkinatorCompletions ? (completions?.length ?? 0) : 0;
    const defaultListPinnedOffset =
      isDefaultList && !isKzDomain ? getDefaultListPinnedOffset(pinnedSuggestions.length) : 0;

    const buttonAction = useSuggestionsButtonAction({
      onMouseEnter,
      onMouseLeave,
      onSelectSuggestion,
      searchQuery,
      isPersonalization: showOnlyCategories,
    });

    if (!suggestions) return null;

    return (
      <>
        {isDefaultList && !isKzDomain ? (
          <SuggestionsDefaultListPinnedItems
            buttonAction={buttonAction}
            index={index}
            isSrp={isSrp}
            suggestionSelector={suggestionSelector}
            pinnedSuggestions={pinnedSuggestions}
          />
        ) : null}

        {showAkinatorCompletions ? (
          <CompletionsAkinator
            completions={completions ?? []}
            navigationIndex={index}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onCompletionClick={(completion) => {
              updateSearchQuery(getUpdatedQueryByCompletionType(searchQuery, completion));
            }}
          />
        ) : null}

        <NewSuggestionsContent
          newSuggestions={newSuggestions}
          hasSponsoredProducts={hasSponsoredProducts}
          showOnlyCategories={showOnlyCategories}
          showProducts={showProducts}
          showBrands={sponsoredBrands.length > 0}
          suggestionSelector={suggestionSelector}
          isDefaultList={isDefaultList}
          searchHref={searchHref}
          index={index}
          isSrp={isSrp}
          isKzDomain={isKzDomain}
          showAkinatorCompletions={showAkinatorCompletions}
          akinatorOffset={akinatorOffset}
          defaultListPinnedOffset={defaultListPinnedOffset}
          searchQuery={searchQuery}
          suggestions={suggestions}
          sponsoredProducts={sponsoredProducts}
          buttonAction={buttonAction}
        />

        <CategoriesList
          suggestionSelector={suggestionSelector}
          isDefaultList={isDefaultList}
          slicedCategories={slicedCategories}
          productsLength={newSuggestions.length}
          suggestions={suggestions}
          index={index}
          isSrp={isSrp}
          buttonAction={buttonAction}
          showOnlyCategories={showOnlyCategories}
          akinatorOffset={akinatorOffset}
          defaultListPinnedOffset={defaultListPinnedOffset}
        />
      </>
    );
  },
);
